import { Component, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnyoCurdComponent } from 'projects/app-core/src/app/models/AnyoCrudComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoUser } from 'projects/app-core/src/auth/models/IAnyoUser';
import { UtilsService } from 'projects/app-core/src/service/utils.service';
import {
  AnyoChallengesCreateReq,
  PhysicalFitnessService,
} from '../../../services/physical-fitness.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { MasterdataService } from '../../../services/masterdata.service';
import { AutocompleteDataSourceItem } from 'ng-zorro-antd/auto-complete';
import { SearchUserResponse } from 'projects/app-core/src/app/session/components/session-table/session-table.component';
import { SessionsService } from 'projects/app-core/src/app/session/service/sessions.service';
import { KinestexExerciseResponse } from '../../../models/fitness/AnyoKinestexExcercise';

interface DropdownOptions {
  value: string;
  data: string;
}

@Component({
  selector: 'app-crud',
  templateUrl: './crud.component.html',
  styleUrl: './crud.component.scss',
})
export class CrudComponent extends AnyoCurdComponent {
  exercisesOptions: DropdownOptions[] = [];
  countDownInSec: number = 0;
  visible = false;
  active = false;
  archive = false;
  showLeaderboard = false;
  isTest = false;
  challengeName = '';
  internalChallengeName = '';
  internalChallengeNameError = false;
  exerciseId = "";
  listOfSelectedValue: string[] = [];
  partners: DropdownOptions[] = [];
  userSearchResults: AutocompleteDataSourceItem[] = [];
  userSearchResultsResponse: SearchUserResponse[] = [];
  selectedUser = '';
  searchSpinner = false;
  users: DropdownOptions[] = [];
  exercises: KinestexExerciseResponse[] = [];
  selectedExercise!: KinestexExerciseResponse;
  isEditable = false;
  challengeId = '';
  description: string = '';

  validateInternalChallengeName = (name: string): boolean => {
    // Must be non-empty, lowercase, and underscore-separated
    return /^[a-z0-9_]+$/.test(name) && name.length > 0;
  };

  constructor(
    protected override auth: AuthService,
    private toastrService: ToastService,
    private masterDataService: MasterdataService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public utils: UtilsService,
    private fitness: PhysicalFitnessService,
    private sessionService: SessionsService,
  ) {
    super(auth);
  }

  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;

  get videoUrlWithCacheBuster() {
    return `${this.selectedExercise.video_URL}?t=${new Date().getTime()}`;
  }

  selectExerciseData($event: string) {
    const exercise = this.exercises.find((data) => data.id === $event);
    console.log(exercise);
    if (exercise) {
      this.selectedExercise = exercise;
      if (this.videoPlayer) {
        const videoElement = this.videoPlayer.nativeElement;
        videoElement.load();
      }
    }
  }

  onUserSelect($event: string) {
    const user = this.userSearchResults.find((data) => data.value === $event);
    if (user) {
      this.users.push({ data: user?.label, value: user?.value });
      this.userIds.push(user.value);
    }
  }

  userIds: string[] = [];
  onSearchUser($event: Event) {
    const searchText = ($event.target as HTMLInputElement).value;
    if (searchText.length > 3) {
      this.searchSpinner = true;
      this.sessionService.adminSearchUsers(searchText).subscribe({
        next: (response) => {
          this.searchSpinner = false;
          this.userSearchResults = [];
          this.userSearchResultsResponse.push(...response);
          response.forEach((value) => {
            if (!this.userIds.includes(value.id)) {
              this.userSearchResults.push({
                value: value.id,
                label: value.email,
              });
            }
          });
        },
        error: (error) => {
          this.searchSpinner = false;
          const errorBody = error.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
        },
      });
    }
  }

  override async create(): Promise<void> {
    this.internalChallengeNameError = !this.validateInternalChallengeName(this.internalChallengeName);
    if (this.internalChallengeNameError) {
      this.toastrService.showAnyoErrorToast('Internal Challenge Name is required and must be lowercase, underscore-separated.');
      return;
    }
    this.pageLoading = true;
    const req: AnyoChallengesCreateReq = {
      visible: this.visible,
      active: this.active,
      isTest: this.isTest,
      archive: this.archive,
      countdown: this.countDownInSec,
      showLeaderboard: this.showLeaderboard,
      exerciseId: this.exerciseId,
      challengeName: this.challengeName,
      internalChallengeName: this.internalChallengeName,
      challengeDescription: this.description,
    };
    if (this.listOfSelectedValue && this.listOfSelectedValue.length) {
      req.applicablePartners = this.listOfSelectedValue;
    }
    if (this.userIds && this.userIds.length) {
      req.applicableUsers = this.userIds;
      req.userDetails = this.users.map((data) => ({
        id: data.value,
        email: data.data
      }));
    }
    this.fitness.createFitnessChallenge(req).subscribe({
      next: () => {
        this.toastrService.showSuccess("successfully created");
        this.router.navigate(['/physical-fitness/challenges/']);
        this.pageLoading = false;
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(errorBody.description);
        this.pageLoading = false;
      }
    });
  }

  override async update(): Promise<void> {
    this.internalChallengeNameError = !this.validateInternalChallengeName(this.internalChallengeName);
    if (this.internalChallengeNameError) {
      this.toastrService.showAnyoErrorToast('Internal Challenge Name is required and must be lowercase, underscore-separated.');
      return;
    }
    const req: AnyoChallengesCreateReq = {
      archive: this.archive,
      visible: this.visible,
      active: this.active,
      isTest: this.isTest,
      countdown: this.countDownInSec,
      showLeaderboard: this.showLeaderboard,
      exerciseId: this.exerciseId,
      challengeName: this.challengeName,
      internalChallengeName: this.internalChallengeName,
      challengeDescription: this.description,
    };
    if (this.listOfSelectedValue && this.listOfSelectedValue.length) {
      req.applicablePartners = this.listOfSelectedValue;
    }
    if (this.userIds && this.userIds.length) {
      req.applicableUsers = this.userIds;
      req.userDetails = this.users.map((data) => ({
        id: data.value,
        email: data.data
      }));
    }
    this.fitness.updateFitnessChallenge(req, this.challengeId).subscribe({
      next: () => {
        this.toastrService.showSuccess("successfully updated");
        this.pageLoading = false;
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(errorBody.description);
        this.pageLoading = false;
      }
    });
  }

  override async get(paramsId: string): Promise<void> {
    this.fitness.fetchFitnessChallengesById(paramsId).subscribe({
      next: (value) => {
        this.active = value.active;
        this.visible = value.visible;
        this.isTest = value.isTest;
        this.challengeName = value.name;
        this.internalChallengeName = value.internalChallengeName;
        this.showLeaderboard = value.showLeaderboard;
        this.countDownInSec = value.countdown;
        this.description = value.challengeDescription;
        this.exerciseId = value.exerciseId;
        this.selectExerciseData(this.exerciseId);
        if (value.applicablePartners) {
          this.listOfSelectedValue = value.applicablePartners;
        }
        if (value.applicableUsers) {
          this.userIds = value.applicableUsers;
        }
        if (value.userDetails) {
          this.users = value.userDetails.map((user) => ({ value: user.id, data: user.email }));
        }
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(errorBody.description);
      }
    });
  }

  override async getData(user?: IAnyoUser | undefined) {
    if (user) {
      this.fitness.fetchAllExercises().subscribe({
        next: (value) => {
          this.exercises = value;
          this.exercisesOptions = value.map((data) => {
            return {
              data: data.title,
              value: data.id,
            };
          });
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
          this.pageLoading = false;
        },
      });
      const id = this.activatedRoute.snapshot.params['id'];
      if (id) {
        this.challengeId = id;
        this.get(id);
        this.isEditable = true;
      }
      
      this.masterDataService.partnerMasterDataList().subscribe({
        next: (value) => {
          this.partners = value.map((data) => {
            return {
              data: data.companyName!,
              value: data._id!,
            };
          });
        },
        error: (err) => {
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(errorBody.description);
        },
      });
    }
  }
}
